import fs from 'node:fs'
import path from 'node:path'

import { runVercel } from './runner.js'
import type { VercelProject, VercelRunner } from './types.js'

/**
 * Sanitize a project name to Vercel's rules: lowercase, only
 * `[a-z0-9._-]`, no leading/trailing separators, max 100 chars.
 */
export function sanitizeVercelProjectName(name: string): string {
  const cleaned = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^[-_.]+/, '')
    .replace(/[-_.]+$/, '')
    .slice(0, 100)
  return cleaned || 'my-app'
}

/** Bounded name search: base plus -v2 … -v10 before giving up on fresh names. */
const MAX_NAME_ATTEMPTS = 10
const INSPECT_TIMEOUT_MS = 30_000

/**
 * Append a version suffix (`-v2`, `-v3`, …) while keeping the full name within
 * Vercel's 100-character limit. Attempt 1 is the base name itself.
 */
export function versionedVercelProjectName(base: string, attempt: number): string {
  if (attempt <= 1) return base
  const suffix = `-v${attempt}`
  return `${base.slice(0, 100 - suffix.length)}${suffix}`
}

/**
 * Create a fresh Vercel project and link the local directory to it (writes
 * `.vercel/project.json`). Reusing an existing project of the same name is
 * deliberately avoided: a reused project usually carries a `DATABASE_URL` env
 * var from a previous run, which makes the Neon integration's connect step fail
 * with a 400. Instead, taken names get a version suffix (`-v2`, `-v3`, …) until
 * a free one is found. Existence is probed with the read-only `project
 * inspect` (exit 0 = taken, scope-consistent with `projects add`); the probe is
 * load-bearing because modern CLIs (v54+) make `projects add` an idempotent
 * success on existing names. An "already exists" error from `projects add`
 * still catches conflicts on older CLIs. Any other `projects add` failure falls back
 * to linking that name anyway — the pre-suffix behavior — so provisioning is
 * never blocked here. The project id is read back from the linked
 * `.vercel/project.json`, the canonical source of truth.
 */
export async function createVercelProject(
  runner: VercelRunner,
  cwd: string,
  name: string,
  env?: NodeJS.ProcessEnv
): Promise<VercelProject> {
  const base = sanitizeVercelProjectName(name)
  let projectName = base

  for (let attempt = 1; attempt <= MAX_NAME_ATTEMPTS; attempt++) {
    const candidate = versionedVercelProjectName(base, attempt)
    projectName = candidate

    const inspect = await runVercel(runner, ['project', 'inspect', candidate], {
      cwd,
      mode: 'capture',
      timeoutMs: INSPECT_TIMEOUT_MS,
      env
    })
    if (inspect.success) continue // name taken → try the next version suffix

    const add = await runVercel(runner, ['projects', 'add', candidate], {
      cwd,
      mode: 'capture',
      env
    })
    if (add.success) break
    if (/already exists/i.test(`${add.stdout}\n${add.stderr}`)) continue
    break // unknown failure — link this name anyway rather than blocking setup
  }

  // Drop stale link state from a previous run: `vercel link` can resolve the
  // project name through the old file's org scope, which may differ from the
  // default scope `inspect`/`add` just used and re-attach to the old project.
  try {
    fs.rmSync(path.join(cwd, '.vercel', 'project.json'), { force: true })
  } catch {
    // best effort — a stale file must never block provisioning
  }

  const link = await runVercel(runner, ['link', '--project', projectName, '--yes'], {
    cwd,
    mode: 'capture',
    env
  })

  return {
    name: projectName,
    linked: link.success,
    projectId: readLinkedProjectId(cwd)
  }
}

/** Project id from `.vercel/project.json`, the canonical link state (undefined when not linked). */
export function readLinkedProjectId(cwd: string): string | undefined {
  return readLinkedProjectJson(cwd)?.projectId
}

/**
 * Project name from `.vercel/project.json` (Vercel CLI v54+ writes it at link
 * time). Used to derive the PUBLIC production alias `https://<name>.vercel.app`
 * — the hashed deployment URL that `vercel deploy` prints (even on its
 * `Production:` line) sits behind Vercel's deployment protection SSO and is the
 * wrong URL to show a user.
 */
export function readLinkedProjectName(cwd: string): string | undefined {
  return readLinkedProjectJson(cwd)?.projectName
}

function readLinkedProjectJson(
  cwd: string
): { projectId?: string; projectName?: string } | undefined {
  try {
    const projectJsonPath = path.join(cwd, '.vercel', 'project.json')
    if (!fs.existsSync(projectJsonPath)) return undefined
    return JSON.parse(fs.readFileSync(projectJsonPath, 'utf-8')) as {
      projectId?: string
      projectName?: string
    }
  } catch {
    return undefined
  }
}
